import { Link } from 'gatsby';
import React, { AnchorHTMLAttributes } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/utils';

import { HemXBadge } from './hem-x-badge';
import { ProductColors } from './product-colors';

type VariantProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  image: JSX.Element;
  hoverImage?: JSX.Element;
  price: string;
  compareAtPrice: string | null;
  discountPercentage: number | null;
  colors?: string[];
  familyName: string;
  productConfig?: string;
  variant?: string;
  isHemX?: boolean;
  isSample?: boolean;
  isSoldOut?: boolean;
  isPreOrder?: boolean;
  isCustom?: boolean;
};

export const Variant = ({
  href,
  image,
  hoverImage,
  price,
  compareAtPrice,
  discountPercentage,
  colors = [],
  className,
  familyName,
  productConfig,
  variant,
  isHemX = false,
  isSample = false,
  isSoldOut = false,
  isPreOrder = false,
  isCustom = false,
  ...props
}: VariantProps) => {
  const configVariant = [productConfig, variant].filter(Boolean).join(', ');

  let salePercentage = null;
  if (discountPercentage >= 15) {
    salePercentage = `-${discountPercentage}%`;
  }

  return (
    <Link
      to={href}
      className={cn(
        'group relative block text-black hover:no-underline',
        className,
      )}
      {...props}
    >
      <div className="relative">
        <div className={cn({ 'group-hover:hidden': !!hoverImage })}>
          {image}
        </div>
        {!!hoverImage && (
          <div className={cn('hidden group-hover:block')}>{hoverImage}</div>
        )}
        {isHemX && <HemXBadge />}
      </div>
      <div className="flex flex-col gap-2 px-4 py-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-pretty text-lg/tight font-bold group-hover:underline">
            {familyName}
          </span>
          <ProductColors colors={colors} />
        </div>
        <div className="flex text-pretty text-base/tight group-hover:underline">
          {configVariant}
        </div>

        {isSoldOut ? (
          <div className="text-base/tight text-black-600">Sold out</div>
        ) : (
          <div className="flex gap-2">
            <span
              className={cn('text-base/tight', {
                'text-black-600': !compareAtPrice,
                'text-red-gloss': compareAtPrice,
              })}
            >
              {price}
            </span>
            {compareAtPrice && (
              <span className="text-base/tight text-black-400 line-through">
                {compareAtPrice}
              </span>
            )}
          </div>
        )}
      </div>
      {!!(isPreOrder || isCustom || salePercentage) && (
        <div className="absolute right-2 top-2 flex flex-col items-end gap-2">
          {isPreOrder && (
            <Badge variant="green-matte" className="inline-flex w-auto">
              Coming soon
            </Badge>
          )}
          {isCustom && (
            <Badge variant="outline" className="inline-flex w-auto">
              Graded-in
            </Badge>
          )}
          {!!salePercentage && !isSample && (
            <Badge variant="red-matte" className="inline-flex w-auto">
              {salePercentage}
            </Badge>
          )}
        </div>
      )}
    </Link>
  );
};
